import { useLocation } from 'react-router-dom';

import { usePageTypeSettingsContext } from 'contexts/page-type-settings/usePageTypeSettings';
import { PageModuleItemType } from 'data/generated/graphql';
import { convertPageModuleItemsToDataModelEntities } from 'utils/allesseh';
import env from 'utils/env';

export function useGetDefaultCollection() {
  const { pageTypeSetting } = usePageTypeSettingsContext();
  const location = useLocation();

  // SETTINGS_ONLY(structure-migration): treatmentTypeSettings comes from hardcoded SETTINGS; not populated for structure pubs
  const treatmentTypeSettings = pageTypeSetting?.treatmentTypeSettings;
  // END SETTINGS_ONLY(structure-migration)

  const getDefaultTreatmentType = (treatmentTypeKey: string) => {
    if (!treatmentTypeSettings) {
      throw new Error('Page type setting or treatment type settings are not defined');
    }

    const treatmentType = treatmentTypeSettings.find((setting) => setting.treatmentType === treatmentTypeKey);

    if (!treatmentType) {
      throw new Error(`Treatment type setting not found for treatment type: ${treatmentTypeKey}`);
    }

    return treatmentType;
  };

  const getDefaultCollection = (treatmentTypeKey: string | undefined) => {
    if (!treatmentTypeKey) {
      throw new Error('Treatment type key is required to get the default collection');
    }

    const treatmentType = getDefaultTreatmentType(treatmentTypeKey);

    if (!treatmentType.module) {
      throw new Error(`Module not found for treatment type: ${treatmentTypeKey}`);
    }

    // Create a mutable copy of the module
    const moduleWithItems = { ...treatmentType.module };

    // TODO: Handle all pre-defined collections using a config instead of hardcoding
    if (treatmentType.treatmentType === 'SUPER_TOPPER_TOP_OPINION') {
      moduleWithItems.moduleItems = [
        {
          itemType: PageModuleItemType.Collection,
          itemFields: {
            contentItem: null,
            queryItem: null,
            collectionItem: null,
            allessehCollectionItem: {
              allessehCollectionId: `${env.UCS_COLLECTION_PREFIX}_WSJOPINION_OFF-PLATFORM_WSJ_HOMEPAGE_TOP`
            },
            linkedItem: null
          }
        }
      ];
    }

    if (treatmentType.treatmentType === 'RIGHT_RAIL_TOP_FREE_EXPRESSION') {
      moduleWithItems.moduleItems = [
        {
          itemType: PageModuleItemType.Collection,
          itemFields: {
            contentItem: null,
            queryItem: null,
            collectionItem: null,
            allessehCollectionItem: {
              allessehCollectionId: `${env.UCS_COLLECTION_PREFIX}_WSJOPINION_OFF-PLATFORM_FREE-EXPRESSION`
            },
            linkedItem: null
          }
        }
      ];
    }

    // Handle Deloitte collections based on the current layout/page
    if (treatmentType.treatmentType === 'DELOITTE_CAMPAIGN') {
      // Map URL slugs/paths to their corresponding Deloitte collection IDs
      const deloitteCollectionMap: Record<string, string> = {
        'sustainable-business': 'Deloitte_SUS_EB_10_1',
        sustainablebusiness: 'Deloitte_SUS_EB_10_1',
        'cio-journal': 'Deloitte_CIO_EB_4_1',
        ciojournal: 'Deloitte_CIO_EB_4_1',
        'cmo-today': 'Deloitte_CMO_EB_8_1',
        cmotoday: 'Deloitte_CMO_EB_8_1',
        'cfo-journal': 'Deloitte_CFO_EB_5_1',
        cfojournal: 'Deloitte_CFO_EB_5_1',
        'risk-compliance-journal': 'Deloitte_RNC_EB_7_1',
        riskcompliancejournal: 'Deloitte_RNC_EB_7_1',
        risk: 'Deloitte_RNC_EB_7_1'
      };

      // Extract the page identifier from the URL path
      // Path format is typically: /wsj/pages/:pageSlugOrId
      const pathname = location.pathname.toLowerCase();
      const pathParts = pathname.split('/').filter(Boolean);
      const pageIdentifier = pathParts[pathParts.length - 1]; // Get the last part of the URL

      // Try to find a matching collection ID based on the page identifier
      let collectionId: string | undefined;

      // First, try exact match
      collectionId = deloitteCollectionMap[pageIdentifier];

      // If no exact match, try to find partial matches
      if (!collectionId) {
        const matchingKey = Object.keys(deloitteCollectionMap).find(
          (key) => pageIdentifier.includes(key) || key.includes(pageIdentifier)
        );
        collectionId = matchingKey ? deloitteCollectionMap[matchingKey] : undefined;
      }

      if (collectionId) {
        moduleWithItems.moduleItems = [
          {
            itemType: PageModuleItemType.Collection,
            itemFields: {
              contentItem: null,
              queryItem: null,
              collectionItem: null,
              allessehCollectionItem: {
                allessehCollectionId: collectionId
              },
              linkedItem: null
            }
          }
        ];
      }
    }

    const collection = convertPageModuleItemsToDataModelEntities(moduleWithItems);

    return collection;
  };

  return {
    getDefaultTreatmentType,
    getDefaultCollection
  };
}
